import React, { useEffect, useRef, useState } from "react";
import logo from "../assets/fumind_smooth.png";
import { getCurrentUser, setMockUser, clearMockUser } from "../utils/userContext";
import { UAT_PERSONAS } from "../data/uatPersonas";
import {
  loadNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../services/notificationService";
import "./header.css";

const POLL_INTERVAL_MS = 60000;
// TEMPORARY TRACK A HTTP TESTING ONLY.
// This Header exposes the seven approved test personas without Microsoft login.
// Remove this temporary entry mechanism before production deployment.

// Temporary test-user login map.
// IMPORTANT: workEmail must match FuMind_EmployeeMaster.workEmail for profile/leave/attendance to load correctly.
const TEST_USERS = UAT_PERSONAS;

function getNotificationId(item) {
  return String(item?.ID || item?.id || item?.notificationId || "");
}

function isUnread(item) {
  return (
    item?.IsRead === false ||
    item?.IsRead === "false" ||
    item?.IsRead === 0 ||
    item?.IsRead === "0" ||
    item?.isRead === false
  );
}

function getNotificationTitle(item) {
  return item?.Title || item?.title || "Notification";
}

function getNotificationMessage(item) {
  return item?.Message || item?.message || item?.Description || item?.Body || "";
}

function formatNotificationTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function playNotificationBeep() {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    const audioContext = new AudioContextClass();
    const oscillator = audioContext.createOscillator();
    const gain = audioContext.createGain();
    oscillator.connect(gain);
    gain.connect(audioContext.destination);
    oscillator.frequency.value = 880;
    gain.gain.setValueAtTime(0.0001, audioContext.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.12, audioContext.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + 0.18);
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.2);
  } catch {
    // Browser may block sound before user interaction. Keep header stable.
  }
}

function Header({ quote }) {

  const [showApps, setShowApps] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [animateBell, setAnimateBell] = useState(false);

  const previousUnreadCount = useRef(0);
  const notificationRefreshInFlight = useRef(false);
  const currentUser = getCurrentUser();

  const currentEmployeeCode = currentUser?.employeeCode || "";
  const currentRole = currentUser?.role || "Guest";
  const selectedTestUserEmail = TEST_USERS.some((item) => item.workEmail === currentUser?.workEmail)
    ? currentUser?.workEmail
    : "";


  const refreshNotifications = async ({ withSound = false } = {}) => {
    if (!currentEmployeeCode || currentRole === "Guest") {
      setNotifications([]);
      setUnreadCount(0);
      previousUnreadCount.current = 0;
      return;
    }

    if (notificationRefreshInFlight.current) return;
    notificationRefreshInFlight.current = true;
    try {
      const loadResult = await loadNotifications({ employeeCode: currentEmployeeCode, role: currentRole, includeRead: true, top: 20 });
      const loadedNotifications = loadResult?.notifications || loadResult?.value || [];
      const safeNotifications = Array.isArray(loadedNotifications) ? loadedNotifications : [];
      const nextUnreadCount = Number(loadResult?.unreadCount ?? safeNotifications.filter(isUnread).length ?? 0);

      if (withSound && nextUnreadCount > previousUnreadCount.current) {
        playNotificationBeep();
        setAnimateBell(true);
        setTimeout(() => setAnimateBell(false), 850);
      }

      previousUnreadCount.current = nextUnreadCount;
      setNotifications(safeNotifications);
      setUnreadCount(nextUnreadCount);
    } catch (error) {
      // Do not break Header if notification flow is offline.
      console.warn("Header notifications unavailable:", error?.message || error);
    } finally {
      notificationRefreshInFlight.current = false;
    }
  };

  useEffect(() => {
    if (!currentEmployeeCode || currentRole === "Guest") {
      setNotifications([]);
      setUnreadCount(0);
      previousUnreadCount.current = 0;
      return undefined;
    }

    refreshNotifications({ withSound: false });
    const interval = setInterval(() => refreshNotifications({ withSound: true }), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEmployeeCode, currentRole]);

  const handleNotificationBellClick = async () => {
    const nextShow = !showNotifications;
    setShowNotifications(nextShow);
    setShowApps(false);

    if (!showNotifications && unreadCount > 0) {
      try {
        await markAllNotificationsRead({ employeeCode: currentEmployeeCode, role: currentRole });
        setUnreadCount(0);
        previousUnreadCount.current = 0;
        setNotifications((previous) => previous.map((item) => ({ ...item, IsRead: true, isRead: true })));
      } catch (error) {
        console.error("Mark all notifications read failed:", error);
      }
    }
  };

  const handleNotificationClick = async (item) => {
    const notificationId = getNotificationId(item);
    if (notificationId && isUnread(item)) {
      try {
        await markNotificationRead(notificationId);
      } catch (error) {
        console.error("Mark notification read failed:", error);
      }
    }

    const route = item?.Route || item?.route;
    if (route) window.location.href = route;
  };

  const handleTestUserChange = (event) => {
    const selected = TEST_USERS.find((item) => item.workEmail === event.target.value);
    if (!selected) return;
    setMockUser(selected);
    window.location.reload();
  };

  const handleClearTestPersona = () => {
    clearMockUser();
    window.location.reload();
  };

  return (
    <>
      <div
        className="top-header"
        onClick={() => {
          if (showApps || showNotifications) {
            setShowApps(false);
            setShowNotifications(false);
          }
        }}
      >
        <div className="header-left">
<div
            className="logo-container"
            role="button"
            tabIndex={0}
            title="Apps"
            aria-label="Open apps menu"
            onClick={(event) => {
              event.stopPropagation();
              setShowApps((s) => !s);
              setShowNotifications(false);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                event.stopPropagation();
                setShowApps((s) => !s);
                setShowNotifications(false);
              }
            }}
          >
            <div className="logo-wrapper">
              <div className="logo-glow" />
              <img src={logo} alt="FuMind" className="logo-3d" />
            </div>
            <div className="app-name1">FuMind.ai</div>
          </div>
        </div>

        <div className="header-center">
          <input placeholder="Search..." />
        </div>

        <div className="header-right">
          <button
            type="button"
            className={`notification-bell ${animateBell ? "is-animating" : ""}`}
            onClick={(event) => {
              event.stopPropagation();
              handleNotificationBellClick();
            }}
            title="Notifications"
          >
            <span aria-hidden="true">🔔</span>
            {unreadCount > 0 && <span className="notification-badge">{unreadCount > 99 ? "99+" : unreadCount}</span>}
          </button>

          <span title="Settings">⚙️</span>
          <span title="Help">❓</span>

          <span
            className="user-name"
            title={currentUser ? `${currentUser.workEmail || ""} (${currentRole})` : "Select a Track A test persona"}
          >
            {currentUser ? `${currentRole} - ${currentEmployeeCode}` : "Track A Testing"}
          </span>

          <select
            aria-label="Select Track A test persona"
            title="Authenticated UAT role and employee persona selector"
            value={selectedTestUserEmail}
            onClick={(event) => event.stopPropagation()}
            onChange={handleTestUserChange}
            style={{
              maxWidth: "240px",
              height: "34px",
              borderRadius: "8px",
              border: "1px solid rgba(255,255,255,0.45)",
              padding: "4px 8px",
              fontSize: "12px",
            }}
          >
            <option value="">Select Test Persona</option>
            {TEST_USERS.map((item) => (
              <option key={item.workEmail} value={item.workEmail}>
                {item.name}
              </option>
            ))}
          </select>

          {currentUser && (
            <button
              type="button"
              className="login-button"
              onClick={(event) => {
                event.stopPropagation();
                handleClearTestPersona();
              }}
              title="Clear the temporary Track A persona"
              style={{ padding: "6px 8px", fontSize: "12px" }}
            >
              Clear
            </button>
          )}
        </div>

        {showApps && (
          <div className="apps-menu" onClick={(event) => event.stopPropagation()}>
            <div className="apps-title">PRODUCTS</div>
            <div className="apps-grid">
              <div className="app-card">
                <div className="app-icon fusion-icon">💾</div>
                <div>
                  <div className="app-name">Fusion</div>
                  <div className="app-type">Product</div>
                </div>
              </div>
              <div className="app-card">
                <div className="app-icon genmes-icon">🏭</div>
                <div>
                  <div className="app-name">GenMES</div>
                  <div className="app-type">Product</div>
                </div>
              </div>
              <div className="app-card">
                <div className="app-icon orbit-icon">🧠</div>
                <div>
                  <div className="app-name">Orbit</div>
                  <div className="app-type">Product</div>
                </div>
              </div>
              <div className="app-card">
                <div className="app-icon edge-icon">📡</div>
                <div>
                  <div className="app-name">EdgeIoT</div>
                  <div className="app-type">Product</div>
                </div>
              </div>
            </div>
            <div className="apps-divider" />
            <div className="apps-title">EXPLORE MORE</div>
            <div className="apps-empty">No additional products</div>
          </div>
        )}

        {showNotifications && (
          <div className="notification-panel-pro" role="dialog" aria-label="Notifications" onClick={(event) => event.stopPropagation()}>
            <div className="notification-panel-header">
              <strong>Notifications</strong>
              <button type="button" onClick={(event) => { event.stopPropagation(); refreshNotifications({ withSound: false }); }}>
                Refresh
              </button>
            </div>

            {notifications.length === 0 && <div className="notification-empty">No notifications found.</div>}

            {notifications.map((item, index) => (
              <button
                type="button"
                key={getNotificationId(item) || `${getNotificationTitle(item)}-${item.CreatedOn || item.createdOn || index}`}
                className={`notification-row ${isUnread(item) ? "unread" : ""}`}
                onClick={(event) => { event.stopPropagation(); handleNotificationClick(item); }}
              >
                <div className="notification-row-title">{getNotificationTitle(item)}</div>
                <div className="notification-row-message">{getNotificationMessage(item) || "No message text available."}</div>
                <div className="notification-row-meta">
                  <span>{item.Module || item.module || "General"}</span>
                  <span>{formatNotificationTime(item.CreatedOn || item.createdOn || item.Created)}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="quote-bar">
        <div className="ticker">
          <span>💡 {quote || "Loading insights..."} 💡</span>
          <span>💡 {quote || "Loading insights..."} 💡</span>
        </div>
      </div>
    </>
  );
}

export default Header;
